import * as React from "react";
import { useDataProvider } from "@refinedev/core";
import {
  EntitlementEventItem,
  type EntitlementHistoryItem,
} from "@/components/EntitlementEventItem";
import { SkeletonTable } from "@/components/SkeletonTable";
import { ErrorBanner } from "@/components/ErrorBanner";
import { EmptyState } from "@/components/EmptyState";
import { CursorPagination } from "@/components/CursorPagination";

interface CurrentEntitlement {
  isPremium: boolean;
  expiresAt: string | null;
  source: "apphud" | "override" | "none";
}

interface EntitlementData {
  effective: CurrentEntitlement;
  history: EntitlementHistoryItem[];
  nextCursor: string | null;
}

interface SubscriptionTimelineProps {
  userId: string;
}

function CurrentEntitlementCard({
  entitlement,
}: {
  entitlement: CurrentEntitlement;
}): React.JSX.Element {
  const sourceLabel: Record<string, string> = {
    apphud: "App Store",
    override: "manual",
    none: "—",
  };

  const formattedExpiry = entitlement.expiresAt
    ? new Intl.DateTimeFormat("ru-RU", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(entitlement.expiresAt))
    : "—";

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] p-5 mb-8">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-[15px] font-[600]">Текущий тариф</h3>
        {entitlement.isPremium ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[12px] font-[600] bg-[rgba(26,138,82,0.10)] text-[var(--success)] before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-[var(--success)]">
            Активна
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[12px] font-[600] bg-[rgba(0,0,0,0.06)] text-[var(--muted)] before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-[var(--muted)]">
            Неактивна
          </span>
        )}
      </div>
      <dl className="grid grid-cols-4 gap-5">
        <div>
          <dt className="text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-1">
            План
          </dt>
          <dd className="text-[14px] font-[600]">
            {entitlement.isPremium ? "Premium" : "Free"}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-1">
            Действует до
          </dt>
          <dd className="text-[14px] font-[600]">{formattedExpiry}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-1">
            Источник
          </dt>
          <dd className="text-[14px] font-[600]">
            {sourceLabel[entitlement.source] ?? entitlement.source}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function SubscriptionTimeline({
  userId,
}: SubscriptionTimelineProps): React.JSX.Element {
  const [cursor, setCursor] = React.useState<string | undefined>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [current, setCurrent] = React.useState<CurrentEntitlement | null>(null);
  const [events, setEvents] = React.useState<EntitlementHistoryItem[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const getDataProvider = useDataProvider();
  const dpRef = React.useRef(getDataProvider());

  React.useEffect(() => {
    dpRef.current = getDataProvider();
  });

  const fetchData = React.useCallback(
    async (loadCursor: string | undefined) => {
      setIsLoading(true);
      setIsError(false);
      try {
        const dp = dpRef.current;
        const params = new URLSearchParams();
        if (loadCursor) params.set("cursor", loadCursor);
        const qs = params.toString();
        const url = `/admin/users/${userId}/entitlement${qs ? `?${qs}` : ""}`;
        const result = await dp.custom?.({
          url,
          method: "get",
        });
        const data = result?.data as EntitlementData | undefined;
        if (data) {
          if (!loadCursor) {
            setCurrent(data.effective ?? null);
            setEvents(data.history ?? []);
          } else {
            setEvents((prev) => [...prev, ...(data.history ?? [])]);
          }
          setNextCursor(data.nextCursor ?? null);
        }
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  React.useEffect(() => {
    void fetchData(undefined);
  }, [fetchData]);

  function handleLoadMore(): void {
    if (nextCursor) {
      setCursor(nextCursor);
      void fetchData(nextCursor);
    }
  }

  if (isLoading && !cursor) {
    return (
      <div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] p-5 mb-8 animate-pulse">
          <div className="flex gap-3 mb-4">
            <div className="h-5 w-32 rounded bg-[var(--surface-2)]" />
            <div className="h-5 w-16 rounded-full bg-[var(--surface-2)]" />
          </div>
          <div className="grid grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i}>
                <div className="h-2.5 w-16 rounded bg-[var(--surface-2)] mb-2" />
                <div className="h-4 w-24 rounded bg-[var(--surface-2)]" />
              </div>
            ))}
          </div>
        </div>
        <SkeletonTable columns={4} rows={5} />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorBanner
        message="Ошибка загрузки данных подписки"
        onRetry={() => {
          setCursor(undefined);
          void fetchData(undefined);
        }}
      />
    );
  }

  if (!current && events.length === 0) {
    return <EmptyState message="Нет истории подписки" />;
  }

  return (
    <div>
      {current && <CurrentEntitlementCard entitlement={current} />}

      {events.length > 0 && (
        <div
          className="relative pl-7 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-0.5 before:bg-[var(--border)] before:rounded-sm"
          role="list"
          aria-label="История подписки"
        >
          {events.map((event: EntitlementHistoryItem) => (
            <EntitlementEventItem key={`${event.date}-${event.type}`} event={event} />
          ))}
        </div>
      )}

      {nextCursor && (
        <div className="pl-7 mt-5">
          <CursorPagination
            nextCursor={nextCursor}
            onLoadMore={handleLoadMore}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
}
